import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/shared";
import JobForm from "@/components/jobs/JobForm";
import SubmitCandidateDialog from "@/components/submissions/SubmitCandidateDialog";
import FileManager from "@/components/files/FileManager";
import RecruiterAssignments from "@/components/jobs/RecruiterAssignments";
import { money, fmtDate, timeAgo, label, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Lock, MapPin, Building2, Send } from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [job, setJob] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [activity, setActivity] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [j, p, a] = await Promise.all([
        api.get(`/jobs/${id}`), api.get(`/jobs/${id}/pipeline`), api.get(`/jobs/${id}/activity`),
      ]);
      setJob(j.data); setPipeline(p.data.items || []); setActivity(a.data.items || []);
    } catch (e) { toast.error(apiErr(e)); navigate("/jobs"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!job) return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;

  const hasCommercial = "recruitment_fee" in job;
  const TABS = [["overview", "Overview"], ["pipeline", "Pipeline"], ["criteria", "Search Criteria"],
    ["recruiters", "Recruiters"], ...(hasCommercial ? [["commercial", "Commercial"]] : []), ["documents", "Documents"], ["activity", "Activity"]];

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/jobs")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to Jobs</button>

      <Card className="p-6" data-testid="job-profile-header">
        <div className="flex flex-wrap items-start gap-5">
          <div className="h-16 w-16 rounded-xl bg-[#255058] text-white grid place-items-center"><Building2 className="h-7 w-7" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl font-bold tracking-tight">{job.title}</h1>
              <StatusBadge value={job.status} /><StatusBadge value={job.priority} />
            </div>
            <div className="text-muted-foreground mt-1">{job.client_name} · {job.requisition_id || "—"}</div>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
              {job.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{job.location}</span>}
              <span>{job.work_model} · {job.employment_type}</span>
              <span>{label(job.staffing_type || "")}</span>
              <span>{job.openings} opening(s)</span>
              <span>{job.days_open ?? 0} days open</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {can("submission:create") && <Button size="sm" onClick={() => setSubmitOpen(true)} data-testid="job-submit-candidate" className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"><Send className="h-4 w-4" /> Submit Candidate</Button>}
            {can("job:edit") && <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="job-edit-button" className="gap-1.5"><Pencil className="h-4 w-4" /> Edit</Button>}
          </div>
        </div>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">{TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`job-tab-${v}`}>{l}</TabsTrigger>)}</TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-5">
              <div className="font-display font-semibold mb-3">Details</div>
              <dl className="space-y-2 text-sm">
                <Row k="Client" v={job.client_name} /><Row k="Region" v={job.region} />
                <Row k="Compensation" v={job.comp_min ? `${money(job.comp_min, job.currency)} – ${money(job.comp_max, job.currency)}` : "—"} />
                <Row k="Required Experience" v={job.required_experience} />
                <Row k="Target Fill Date" v={fmtDate(job.target_fill_date)} />
                <Row k="Job Owner" v={job.job_owner_name} />
                <Row k="Target Start Date" v={fmtDate(job.target_start_date)} />
                <Row k="Interview Process" v={job.interview_process} />
                <Row k="Recruiter Instructions" v={job.submission_instructions} />
                {job.status === "on_hold" && <Row k="Hold Reason" v={job.hold_reason} />}
                {job.status === "closed" && <Row k="Close Reason" v={job.close_reason} />}
              </dl>
            </Card>
            <Card className="p-5">
              <div className="font-display font-semibold mb-3">Description</div>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{job.description || "No description provided."}</p>
              {job.internal_notes && <div className="mt-4 pt-4 border-t border-border"><div className="text-xs font-semibold text-muted-foreground mb-1">Internal Notes</div><p className="text-sm">{job.internal_notes}</p></div>}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pipeline" className="mt-4">
          <Card className="overflow-hidden">
            {pipeline.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No candidates in pipeline yet. Use "Submit Candidate" to add one.</div>
              : (<div className="divide-y divide-border">
                {pipeline.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 p-3 hover:bg-accent/40 cursor-pointer" onClick={() => navigate(`/candidates/${s.candidate_id}`)}>
                    <Avatar className="h-9 w-9"><AvatarFallback className="bg-[#255058] text-white text-xs">{initials(s.candidate_name)}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0"><div className="font-medium truncate">{s.candidate_name}</div><div className="text-xs text-muted-foreground">{s.recruiter_name} · {timeAgo(s.submitted_at)}</div></div>
                    <StatusBadge value={s.stage} />
                  </div>
                ))}
              </div>)}
          </Card>
        </TabsContent>

        <TabsContent value="criteria" className="mt-4">
          <Card className="p-5 space-y-4">
            <div><div className="text-xs font-semibold text-muted-foreground mb-2">Required Skills</div><div className="flex flex-wrap gap-2">{(job.required_skills || []).map((s) => <Badge key={s} className="status-teal border-0">{s}</Badge>)}{!(job.required_skills || []).length && <span className="text-sm text-muted-foreground">—</span>}</div></div>
            <div><div className="text-xs font-semibold text-muted-foreground mb-2">Preferred Skills</div><div className="flex flex-wrap gap-2">{(job.preferred_skills || []).map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}{!(job.preferred_skills || []).length && <span className="text-sm text-muted-foreground">—</span>}</div></div>
            <Row k="Work Authorization" v={job.work_authorization} /><Row k="Education" v={job.education} />
            <Row k="Certifications" v={job.certifications} />
            {!!(job.screening_questions || []).length && (
              <div><div className="text-xs font-semibold text-muted-foreground mb-2">Screening Questions</div>
                <ol className="list-decimal pl-5 text-sm space-y-1">{job.screening_questions.map((q, i) => <li key={i}>{q.question}</li>)}</ol></div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="recruiters" className="mt-4">
          <RecruiterAssignments job={job} onChanged={load} />
        </TabsContent>

        {hasCommercial && (
          <TabsContent value="commercial" className="mt-4">
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3 font-display font-semibold"><Lock className="h-4 w-4 text-[#255058]" /> Commercial Terms</div>
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <Row k="Recruitment Fee" v={job.recruitment_fee ? `${job.recruitment_fee}${job.fee_model === "percentage" ? "%" : ""}` : "—"} />
                <Row k="Fee Model" v={job.fee_model} /><Row k="Payment Terms" v={job.payment_terms} />
                <Row k="Guarantee Period" v={job.guarantee_period} /><Row k="Estimated Revenue" v={money(job.estimated_revenue, job.currency)} />
                <Row k="Bill Rate" v={money(job.bill_rate, job.currency)} /><Row k="Pay Rate" v={money(job.pay_rate, job.currency)} />
                <Row k="Billing Entity" v={job.billing_entity} />
              </dl>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="documents" className="mt-4">
          <FileManager recordType="job" recordId={job.id} canWrite={can("job:edit")} title="Job Documents" />
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <Card className="p-5"><div className="space-y-3">
            {activity.length ? activity.map((a) => (
              <div key={a.id} className="flex gap-3 text-sm"><div className="h-1.5 w-1.5 rounded-full bg-[#255058] mt-1.5 shrink-0" /><div><div>{a.summary}</div><div className="text-[11px] text-muted-foreground">{a.actor_name} · {timeAgo(a.created_at)}</div></div></div>
            )) : <div className="text-sm text-muted-foreground">No activity yet.</div>}
          </div></Card>
        </TabsContent>
      </Tabs>

      <JobForm open={editOpen} job={job} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />
      <SubmitCandidateDialog open={submitOpen} jobId={job.id} jobTitle={job.title} onClose={() => setSubmitOpen(false)} onDone={() => { setSubmitOpen(false); load(); }} />
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium text-right">{v || "—"}</dd></div>;
}
